<script lang="ts">
  /**
   * App shell — routes between FirstRunWizard, MainMenu, WorldCreation,
   * CharacterCreation, and GameScreen based on the game state machine.
   */
  import { isFirstRun } from "$lib/stores/firstRun";
  import { gameStore, worldCreationStore } from "$lib/stores/game.svelte";
  import {
    createWorldWithSetting,
    listWorlds,
    loadWorld,
    deleteWorld,
    getWorldState,
    getCharacter,
  } from "$lib/api/invoke";
  import type { WorldCreationParams } from "$lib/api/invoke";
  import type { ResumeData } from "$lib/components/GameScreen.svelte";
  import { keyCache } from "$lib/stores/keys";
  import FirstRunWizard from "$lib/components/FirstRunWizard.svelte";
  import MainMenu from "$lib/components/MainMenu.svelte";
  import ManageWorlds from "$lib/components/ManageWorlds.svelte";
  import WorldCreationWizard from "$lib/components/WorldCreationWizard.svelte";
  import CharacterCreation from "$lib/components/CharacterCreation.svelte";
  import GameScreen from "$lib/components/GameScreen.svelte";
  import OverlayContainer from "$lib/components/overlays/OverlayContainer.svelte";
  import { overlayStore } from "$lib/stores/game.svelte";

  let showWizard = $state(isFirstRun());
  let loadError = $state("");
  let globalError = $state("");
  let resumeData = $state<ResumeData | undefined>(undefined);
  let showManageWorlds = $state(false);
  /** ID of the world currently being created (for cleanup on back). */
  let pendingWorldId = $state<string | null>(null);

  // Clear stale key cache entries on app init so fresh lookups go to Rust/mock.
  keyCache.clearAll();

  // Global unhandled promise rejection handler.
  function handleUnhandledRejection(e: PromiseRejectionEvent): void {
    e.preventDefault();
    const msg = e.reason instanceof Error ? e.reason.message : String(e.reason);
    console.error("[Unhandled rejection]", msg);
    globalError = msg;
    // Auto-clear after 10 seconds.
    setTimeout(() => {
      globalError = "";
    }, 10000);
  }

  const isInGame = $derived(
    gameStore.state === "story" ||
      gameStore.state === "input-freeform" ||
      gameStore.state === "input-guided" ||
      gameStore.state === "loading" ||
      gameStore.state === "idle-waiting" ||
      gameStore.state === "opening-scene",
  );

  function handleWizardComplete(): void {
    showWizard = false;
  }

  function handleNewWorld(): void {
    worldCreationStore.reset();
    gameStore.transition("world-creation");
  }

  async function handleWorldCreated(params: WorldCreationParams): Promise<void> {
    loadError = "";
    try {
      const world = await createWorldWithSetting(params);
      pendingWorldId = world.id;
      worldCreationStore.worldName = params.name;
      worldCreationStore.worldSetting = params.settingPrompt;
      worldCreationStore.genre = params.genre;
      worldCreationStore.era = params.era;
      worldCreationStore.tone = params.tone;
      // World created successfully — world.id stored by engine
      gameStore.transition("character-creation");
    } catch (err) {
      loadError = String(err);
      console.error("Failed to create world:", err);
    }
  }

  function handleCharacterDone(): void {
    pendingWorldId = null; // World is no longer "pending" — player committed
    gameStore.transition("opening-scene");
  }

  function handleBackToMenu(): void {
    // Clean up incomplete world if the player backs out before finishing
    if (pendingWorldId) {
      deleteWorld(pendingWorldId).catch((err) =>
        console.warn("Failed to delete incomplete world:", err),
      );
      pendingWorldId = null;
    }
    gameStore.state = "main-menu";
  }

  async function handleBackToWorldCreation(): Promise<void> {
    // Delete the incomplete world since the player is going back to reconfigure
    if (pendingWorldId) {
      try {
        await deleteWorld(pendingWorldId);
      } catch (err) {
        console.warn("Failed to delete incomplete world:", err);
      }
      pendingWorldId = null;
    }
    gameStore.state = "world-creation";
  }

  /** Resume the most recent played world (turn > 0). */
  async function handleResume(): Promise<void> {
    loadError = "";
    try {
      const worlds = await listWorlds();
      if (!worlds || worlds.length === 0) {
        loadError = "No saved worlds found.";
        return;
      }
      // Sort by updated_at descending
      worlds.sort((a: { updated_at: string }, b: { updated_at: string }) =>
        b.updated_at.localeCompare(a.updated_at),
      );

      // Find the most recent world that has actually been played (turn > 0).
      // We need to check each world's state to find one with progress.
      let found = false;
      for (const candidate of worlds) {
        await loadWorld(candidate.id);
        const ws = await getWorldState();
        if (ws.turn > 0 || ws.last_narrative) {
          // This world has progress — resume it
          worldCreationStore.worldName = ws.world_name || candidate.name;
          worldCreationStore.worldSetting = ws.setting_prompt || ws.world_name || candidate.name;

          let charName = ws.character_name || "Adventurer";
          let charBackground = "";
          try {
            const charJson = await getCharacter();
            const char = JSON.parse(charJson);
            if (char.name) charName = char.name;
            if (char.background) charBackground = char.background;
          } catch {
            // non-fatal — use defaults
          }

          resumeData = {
            characterName: charName,
            characterBackground: charBackground,
            turnNumber: ws.turn || 0,
            currentLocation: ws.location || "Unknown location",
            recentActions: ws.recent_actions || [],
            lastNarrative: ws.last_narrative || "",
          };

          gameStore.transition("loading");
          gameStore.state = "input-guided";
          found = true;
          break;
        }
      }

      if (!found) {
        loadError = "No played worlds to resume. Start a New World first.";
      }
    } catch (err) {
      loadError = `Failed to resume: ${String(err)}`;
      console.error("Resume failed:", err);
    }
  }

  /** Open the Manage Worlds overlay. */
  function handleManageWorlds(): void {
    showManageWorlds = true;
  }

  /** Resume a specific world from the Manage Worlds overlay. */
  function handleManageWorldResume(data: ResumeData): void {
    showManageWorlds = false;
    resumeData = data;
    gameStore.transition("loading");
    gameStore.state = "input-guided";
  }

  function handleQuit(): void {
    resumeData = undefined;
    pendingWorldId = null;
    gameStore.transition("main-menu");
  }
</script>

<svelte:window onunhandledrejection={handleUnhandledRejection} />

<div class="app-shell">
  {#if showWizard}
    <FirstRunWizard onComplete={handleWizardComplete} />
  {:else if gameStore.state === "world-creation"}
    <WorldCreationWizard onComplete={handleWorldCreated} onBack={handleBackToMenu} />
    {#if loadError}
      <div class="error">{loadError}</div>
    {/if}
  {:else if gameStore.state === "character-creation"}
    <CharacterCreation
      worldSetting={worldCreationStore.worldSetting || worldCreationStore.assembledPrompt}
      onComplete={handleCharacterDone}
      onBack={handleBackToWorldCreation}
    />
  {:else if isInGame}
    {#if gameStore.state === "loading"}
      <div class="loading">Creating world...</div>
    {:else}
      <GameScreen
        onQuit={handleQuit}
        isOpeningScene={gameStore.state === "opening-scene"}
        worldSetting={worldCreationStore.worldSetting || worldCreationStore.assembledPrompt}
        {resumeData}
      />
    {/if}
  {:else}
    <MainMenu
      onResume={handleResume}
      onNewWorld={handleNewWorld}
      onManageWorlds={handleManageWorlds}
      onQuit={() => {}}
    />
    {#if loadError}
      <div class="error">{loadError}</div>
    {/if}
    {#if showManageWorlds}
      <ManageWorlds
        onResume={handleManageWorldResume}
        onClose={() => {
          showManageWorlds = false;
        }}
      />
    {/if}
    <!-- Overlay container for settings from main menu -->
    <OverlayContainer activeOverlay={overlayStore.active} onClose={() => overlayStore.close()} />
  {/if}

  {#if globalError}
    <div class="global-error">
      <span>[!] {globalError}</span>
      <button
        class="global-error__dismiss"
        onclick={() => {
          globalError = "";
        }}>[x]</button
      >
    </div>
  {/if}
</div>

<style>
  .app-shell {
    height: 100%;
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 1.2rem;
  }

  .error {
    position: fixed;
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    padding: 0.5rem 1rem;
    background: #330000;
    color: #ff4444;
    font-family: var(--font-mono);
    font-size: 0.85rem;
    border: 1px solid #ff4444;
  }

  .global-error {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    padding: 0.4rem 1rem;
    background: #330000;
    color: #ff4444;
    font-family: var(--font-mono);
    font-size: 0.8rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 9999;
    border-bottom: 1px solid #ff4444;
  }

  .global-error__dismiss {
    background: none;
    border: none;
    color: #ff4444;
    font-family: var(--font-mono);
    cursor: pointer;
  }
</style>
